// WorkList (a pattern, truck stock BL-203): a page's column of work sent to you, under its heading and
// count: one RecordRow each (what it is, what it says now, the one way in at its end), each opening its
// working view, which the page passes in (a slot): in place, under its row, or beside the page in a
// SidePanel (wide, for a view with lines), the row marked while it's open; Escape or Close gives the
// focus back to the row. Read only (the sender's own view: "With the office"), each opens in place with
// no way in at its end. ↑ ↓ move between the rows (RecordList). Its first use is the Stock page's column
// "From Sam": a tech's count, tomorrow's refill, a restock received short, a receipt to file. It's new
// because RecordList draws rows but not the panel and the focus coming back, and ReviewQueue steps
// through one item at a time. Reads only --worklist-*.
import { useId, useState, type ReactNode } from 'react';
import { RecordList, RecordRow } from '../RecordRow/RecordRow.tsx';
import { SidePanel } from '../SidePanel/SidePanel.tsx';
import './WorkList.css';

export interface WorkListItem {
  id: string;
  /** What it is: "His count of the van", "Tomorrow’s refill, TR-0419". */
  title: string;
  /** What it says now, muted: "Sent 10:52 a.m.: 3 differ from what it shows, 1 not on his list". */
  says: string;
  /** The way in, at the row's end: "Review and post", "Check and send". */
  action?: string;
  /** in-place: opens under its row; panel: beside the page, in a SidePanel. */
  opens: 'in-place' | 'panel';
  /** Its working view (a slot): on the Stock page, CountReview's note and reply, RefillLines,
   *  RestockReceived, ReceiptFiling. */
  body: ReactNode;
  /** A panel's first line ("Tomorrow’s refill: TR-0419") and its muted line ("For Sam’s van, from Sam K."). */
  panelTitle?: string;
  panelSub?: string;
  /** A view with lines (the refill, a receipt) opens in the wide panel, the window's height. */
  wide?: boolean;
}
export interface WorkListProps {
  /** "From Sam"; the tech's own: "With the office". */
  heading: string;
  items: WorkListItem[];
  /** The tech's own: each opens in place, read only, with no way in at its end. */
  readOnly?: boolean;
  /** Open as it draws (the page was opened at it). */
  defaultOpen?: string | null;
  onOpenChange?: (id: string | null) => void;
}

export function WorkList({ heading, items, readOnly, defaultOpen = null, onOpenChange }: WorkListProps) {
  const uid = useId();
  const [open, setOpen] = useState<string | null>(defaultOpen);
  const set = (id: string | null) => {
    setOpen(id);
    onOpenChange?.(id);
  };
  const panel = items.find((i) => i.id === open && i.opens === 'panel' && !readOnly);
  return (
    <section className="fl-worklist" aria-labelledby={`${uid}-h`}>
      <h2 className="fl-worklist__h" id={`${uid}-h`}>
        {heading} <span className="fl-worklist__count">{items.length}</span>
      </h2>
      <RecordList label={heading}>
        {items.map((i) =>
          readOnly || i.opens === 'in-place' ? (
            <RecordRow
              key={i.id}
              id={`${uid}-${i.id}`}
              title={i.title}
              sub={i.says}
              end={readOnly ? undefined : i.action}
              onToggle={() => set(open === i.id ? null : i.id)}
              isOpen={open === i.id}
              expanded={<div className="fl-worklist__body">{i.body}</div>}
            />
          ) : (
            <RecordRow
              key={i.id}
              id={`${uid}-${i.id}`}
              title={i.title}
              sub={i.says}
              end={i.action}
              label={`${i.title}: ${i.action ?? 'open'}`}
              onOpen={() => set(i.id)}
              isOpen={open === i.id}
            />
          ),
        )}
      </RecordList>
      {panel ? (
        <SidePanel
          label={panel.panelTitle ?? panel.title}
          title={panel.panelTitle ?? panel.title}
          sub={panel.panelSub}
          width={panel.wide ? 'wide' : undefined}
          takesFocus
          primary={null}
          onClose={() => {
            set(null);
            requestAnimationFrame(() => document.getElementById(`${uid}-${panel.id}`)?.focus());
          }}
        >
          <div className="fl-worklist__panel">{panel.body}</div>
        </SidePanel>
      ) : null}
    </section>
  );
}
